import type { DropdownProps } from 'primereact/dropdown';

type PtPart = object | ((options: unknown) => object) | undefined;

export interface DropdownAccessibilityInput {
  ariaLabel?: string;
  ariaLabelledBy?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  placeholder?: string;
  disabled?: boolean;
  pt?: DropdownProps['pt'];
}

export interface DropdownAccessibility {
  /** Spread onto the Dropdown: names its keyboard focus input. */
  nameAttrs: { 'aria-label'?: string; 'aria-labelledby'?: string };
  /** PrimeReact's own props: name the trigger. */
  ariaLabel: string | undefined;
  ariaLabelledBy: string | undefined;
  /** The caller's pass-through, completed for the root, hidden select and trigger. */
  pt: DropdownProps['pt'];
}

const merge = (own: PtPart, attrs: object, ownWins: boolean): PtPart => {
  if (typeof own === 'function') {
    return (options: unknown) => (ownWins ? { ...attrs, ...own(options) } : { ...own(options), ...attrs });
  }
  return ownWins ? { ...attrs, ...own } : { ...own, ...attrs };
};

/** `aria-labelledby` when there is one, else `aria-label`, else nothing. */
export function nameAttrsFor(labelledBy?: string, label?: string): DropdownAccessibility['nameAttrs'] {
  if (labelledBy) return { 'aria-labelledby': labelledBy };
  if (label) return { 'aria-label': label };
  return {};
}

/**
 * Completes the accessibility PrimeReact's Dropdown (10.9) leaves unfinished, for every
 * design-system atom that wraps it.
 *
 * - One accessible name for all three announced parts. Left to itself, Dropdown names only the
 *   trigger (`ariaLabel || placeholder`), names the keyboard focus input only from an explicit
 *   `aria-label`/`aria-labelledby` prop, and names the hidden native `<select>` from nothing —
 *   so a dropdown showing a value, with no placeholder, is announced as an unnamed button, an
 *   unlabelled text field and an unnamed select (axe `aria-command-name`, `label`,
 *   `select-name`). A caller names the control once — `aria-labelledby` pointing at its
 *   visible label, or `ariaLabel` / `aria-label` — and the name reaches every part;
 *   `placeholder` is the fallback.
 * - PrimeReact stamps `aria-activedescendant="dropdownItem_N"` on the ROOT `<div>` while
 *   focused: a role that does not support the attribute (axe `aria-allowed-attr`), pointing at
 *   an id no option carries. It announces nothing, so it is dropped.
 * - A disabled dropdown marks the whole widget disabled, not only its inputs, so the value it
 *   shows reads as part of an inactive control (it is dimmed with the rest of it).
 * - When the consumer names the control the plain HTML way — `<label htmlFor>` pointing at
 *   `inputId` — that names the focus input and nothing else, because the label element's own
 *   id is not knowable from here. The hidden native `<select>` (a form-submission shim
 *   carrying `tabindex="-1"`) and the `.p-dropdown-trigger` (`role="button"`, a chevron that
 *   duplicates the combobox's own popup affordance) are then left unnamed, which axe reports
 *   as `select-name` and `aria-command-name`. Neither is a control a screen-reader user
 *   operates — the combobox is — so with no name to give them they are removed from the
 *   accessibility tree rather than announced anonymously. An explicit name still wins.
 */
export function dropdownAccessibility(input: DropdownAccessibilityInput): DropdownAccessibility {
  const ariaLabelledBy = input['aria-labelledby'] ?? input.ariaLabelledBy ?? undefined;
  const ariaLabel = ariaLabelledBy ? undefined : input['aria-label'] ?? input.ariaLabel ?? input.placeholder ?? undefined;
  const nameAttrs = nameAttrsFor(ariaLabelledBy, ariaLabel);

  const callerPt = (input.pt ?? {}) as Record<string, PtPart>;
  const rootAttrs = { 'aria-activedescendant': undefined, 'aria-disabled': input.disabled ? true : undefined };
  // Named when there is a name to give; otherwise removed, never announced anonymously.
  const named = Boolean(ariaLabelledBy || ariaLabel);
  const selectAttrs = named ? nameAttrs : { 'aria-hidden': true };
  const triggerAttrs = named ? nameAttrs : { 'aria-hidden': true, role: undefined };
  const pt = {
    ...callerPt,
    root: merge(callerPt.root, rootAttrs, false),
    select: merge(callerPt.select, selectAttrs, true),
    trigger: merge(callerPt.trigger, triggerAttrs, true),
  } as DropdownProps['pt'];

  return { nameAttrs, ariaLabel, ariaLabelledBy, pt };
}
